import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Copy } from "lucide-react";
import { api, eur } from "../lib/api";
import { Navigation } from "../components/Navigation";
import { useCart } from "../context/CartContext";

const MAX_ATTEMPTS = 8;
const POLL_MS = 2000;

export default function CheckoutSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [order, setOrder] = useState(null);
  const [copied, setCopied] = useState(false);
  const { clear } = useCart();

  useEffect(() => {
    document.title = "Danke · Die Lichtmalerin";
    window.scrollTo(0, 0);
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let attempts = 0;
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setOrder(data);
          setStatus("paid");
          clear();
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        if (attempts >= MAX_ATTEMPTS) {
          setStatus("timeout");
          setOrder(data);
          return;
        }
        attempts += 1;
        setTimeout(poll, POLL_MS);
      } catch {
        setStatus("error");
      }
    };
    poll();

    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const copyOrder = () => {
    if (!order?.order_number) return;
    navigator.clipboard.writeText(order.order_number);
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  };

  return (
    <div className="min-h-screen bg-[#090909]" data-testid="checkout-success-page">
      <Navigation />

      <section className="pt-28 md:pt-40 pb-24 px-6 lg:px-12">
        <div className="max-w-2xl mx-auto text-center">
          {status === "checking" && (
            <div data-testid="status-checking">
              <p className="text-[#A1A1AA] font-sans">
                Zahlung wird bestätigt…
              </p>
            </div>
          )}

          {status === "paid" && order && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.2 }}
              data-testid="status-paid"
            >
              <div className="flex justify-center mb-8 text-[#D4AF37]">
                <CheckCircle2 size={56} strokeWidth={1} />
              </div>
              <span className="gold-divider">Danke</span>
              <h1 className="font-serif font-light text-4xl sm:text-5xl leading-[1.1] tracking-tight text-[#E5E5E5] mt-8">
                Ihr Licht ist{" "}
                <span className="italic gold-text">auf dem Weg.</span>
              </h1>
              <p className="mt-6 text-[#E5E5E5]/70 font-sans font-light leading-relaxed">
                Vielen Dank für Ihre Bestellung. Nicole erhält gerade eine
                Benachrichtigung und wird sich in Kürze um den Versand kümmern.
              </p>

              <div className="mt-12 inline-flex flex-col items-center gap-3 border border-[#D4AF37]/25 px-8 py-6 bg-[#0b0b0b]">
                <p className="text-[10px] uppercase tracking-[0.4em] text-[#A1A1AA] font-sans">
                  Ihre Bestellnummer
                </p>
                <p
                  className="font-serif text-3xl text-[#D4AF37] tabular-nums"
                  data-testid="success-order-number"
                >
                  {order.order_number}
                </p>
                <button
                  onClick={copyOrder}
                  className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.35em] font-sans font-light text-[#A1A1AA] hover:text-[#D4AF37] transition-colors"
                  data-testid="success-copy-order"
                >
                  <Copy size={12} /> {copied ? "Kopiert" : "Nummer kopieren"}
                </button>
              </div>

              <p className="mt-8 text-xs text-[#A1A1AA] font-sans max-w-md mx-auto leading-relaxed">
                Bewahren Sie diese Nummer bitte auf — Sie benötigen sie für den
                Widerruf innerhalb der 14-tägigen Frist.
              </p>

              <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/shop"
                  className="btn-gold px-8 py-3 font-sans font-light tracking-widest text-xs uppercase"
                >
                  Weiter stöbern
                </Link>
                <Link
                  to="/widerruf"
                  className="text-[11px] uppercase tracking-[0.35em] font-sans font-light text-[#A1A1AA] hover:text-[#D4AF37] transition-colors"
                >
                  Widerruf einreichen
                </Link>
              </div>
            </motion.div>
          )}

          {status === "expired" && (
            <div data-testid="status-expired">
              <p className="text-[#E5E5E5] font-serif text-2xl">
                Die Zahlung wurde nicht abgeschlossen.
              </p>
              <Link
                to="/cart"
                className="mt-6 inline-block text-[#D4AF37] hover:text-[#FDE047] uppercase tracking-[0.3em] text-xs font-sans"
              >
                Zurück zum Warenkorb
              </Link>
            </div>
          )}

          {(status === "timeout" || status === "error") && (
            <div data-testid="status-pending">
              <p className="text-[#E5E5E5] font-serif text-2xl">
                Wir prüfen noch Ihre Zahlung.
              </p>
              <p className="mt-4 text-[#A1A1AA] font-sans">
                Sie erhalten eine Bestätigung per E-Mail, sobald sie eingegangen ist.
              </p>
              <Link
                to="/shop"
                className="mt-6 inline-block text-[#D4AF37] hover:text-[#FDE047] uppercase tracking-[0.3em] text-xs font-sans"
              >
                Zurück zum Shop
              </Link>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
